import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminGet, adminPost } from "@/lib/admin-api";

interface Invoice {
  id: string;
  invoiceNumber: string;
  donorEmail: string;
  donorName: string;
  total: number;
  currency: string;
  status: string;
  issueDate: string;
  dueDate: string;
  paymentUrl: string;
  primaryWishItemId?: string;
}

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  paid: "Paid",
  overdue: "Overdue",
  refunded: "Refunded",
  cancelled: "Cancelled",
  failed: "Failed",
};

function InvoicesAdminInner() {
  const [items, setItems] = React.useState<Invoice[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string>("");

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const url = status ? `/api/admin/invoices/list?status=${status}` : "/api/admin/invoices/list";
      const res = await adminGet<{ items: Invoice[] }>(url);
      setItems(res.items);
    } catch (err) {
      setError(String(err));
    }
  }, [status]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function onSend(invoiceId: string) {
    if (!confirm("Send the payment-link email to this donor now?")) return;
    try {
      await adminPost(`/api/admin/invoices/send`, { id: invoiceId });
      await load();
    } catch (err) {
      setError(String(err));
    }
  }

  async function onDelete(invoiceId: string) {
    if (!confirm("Delete this invoice (and the underlying WC order)? This is destructive.")) return;
    try {
      await adminPost(`/api/admin/invoices/delete`, { id: invoiceId });
      await load();
    } catch (err) {
      setError(String(err));
    }
  }

  // Stat tiles
  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = items?.filter((i) => i.status !== "paid" && i.dueDate < today).length ?? 0;
  const dueSoonCount =
    items?.filter((i) => {
      if (i.status === "paid") return false;
      const due = new Date(i.dueDate);
      const diffDays = (due.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diffDays >= 0 && diffDays <= 7;
    }).length ?? 0;
  const paid30Count =
    items?.filter((i) => {
      if (i.status !== "paid") return false;
      const issued = new Date(i.issueDate);
      const diffDays = (Date.now() - issued.getTime()) / (1000 * 60 * 60 * 24);
      return diffDays <= 30;
    }).length ?? 0;

  return (
    <PageShell title="Invoices · Admin" status="admin" width="wide">
      <Seo title="Invoices admin" description="" path="/admin/invoices" noindex />
      <AdminTopNav />

      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <h2 className="font-serif text-xl text-ink">Invoices</h2>
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-hairline bg-paper px-3 py-2 font-mono text-xs"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <Link
            to="/admin/invoices/new"
            className="bg-ink text-paper px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-ink-2"
          >
            + New invoice
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-hairline bg-paper-2 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">
            Overdue
          </div>
          <div className="font-mono text-2xl text-state-pending">{overdueCount}</div>
        </div>
        <div className="border border-hairline bg-paper-2 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">
            Due this week
          </div>
          <div className="font-mono text-2xl text-ink">{dueSoonCount}</div>
        </div>
        <div className="border border-hairline bg-paper-2 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">
            Paid (30d)
          </div>
          <div className="font-mono text-2xl text-claude">{paid30Count}</div>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      {items === null && !error && (
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
      )}

      {items && items.length === 0 && (
        <p className="font-serif text-sm text-ink-2">
          No invoices yet.{" "}
          <Link to="/admin/invoices/new" className="text-claude underline">
            Create the first one →
          </Link>
        </p>
      )}

      {items && items.length > 0 && (
        <div className="border border-hairline overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
              <tr className="border-b border-hairline">
                <th className="text-left px-4 py-3 font-bold">#</th>
                <th className="text-left px-4 py-3 font-bold">Donor</th>
                <th className="text-right px-4 py-3 font-bold">Total</th>
                <th className="text-left px-4 py-3 font-bold">Issued</th>
                <th className="text-left px-4 py-3 font-bold">Due</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline text-ink-2">
              {items.map((inv) => (
                <tr key={inv.id}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link
                      to={`/admin/invoices/${inv.id}`}
                      className="text-ink hover:text-claude underline decoration-hairline"
                    >
                      {inv.invoiceNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className="block">{inv.donorName}</span>
                    <span className="block font-mono text-[10px] text-ink-3">
                      {inv.donorEmail}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    US${inv.total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{inv.issueDate}</td>
                  <td className="px-4 py-3 font-mono text-xs">{inv.dueDate}</td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                    {STATUS_LABEL[inv.status] ?? inv.status}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest space-x-3">
                    <button onClick={() => onSend(inv.id)} className="text-ink hover:text-claude">
                      Send
                    </button>
                    <a href={inv.paymentUrl} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-claude">
                      Pay link
                    </a>
                    <button onClick={() => onDelete(inv.id)} className="text-state-pending hover:text-ink">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

export function InvoicesAdmin() {
  return (
    <AdminGuard allow={["admin"]}>
      <InvoicesAdminInner />
    </AdminGuard>
  );
}
